import { useState, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { FormikErrors } from 'formik';
import { abyssColors } from '@/theme/colors';
import { BulkEditColumnDef, BulkEditFormValues } from './types';
import { BulkEditCell } from './BulkEditCell';
import { BulkColumnEditPopover } from './BulkColumnEditPopover';
import { ColumnsSelectionDialog } from '@/components/Analysis/Insights/ColumnsSelectionDialog';
import { BulkEditPointOfInterestsQuery } from '@/__generated__/graphql';

type Poi = BulkEditPointOfInterestsQuery['pointOfInterests'][number];

type Props = {
  pois: Poi[];
  columns: BulkEditColumnDef[];
  values: BulkEditFormValues;
  errors: FormikErrors<BulkEditFormValues>;
  setFieldValue: (field: string, value: string) => void;
  setValues: (values: BulkEditFormValues) => void;
  visibleColumnKeys: string[];
  onVisibleColumnsChange: (keys: string[]) => void;
  initialValues: BulkEditFormValues;
};

const MANDATORY_COL_WIDTHS: Record<string, number> = {
  poiId: 100,
  name: 180,
  assembly: 140,
};

const thSx = {
  padding: '8px 12px',
  textAlign: 'center' as const,
  fontSize: '1.2rem',
  fontWeight: 600,
  color: abyssColors.primary[400],
  border: `1px solid ${abyssColors.primary[100]}`,
  whiteSpace: 'nowrap' as const,
  backgroundColor: '#fff',
  position: 'sticky' as const,
  top: 0,
  zIndex: 1,
};

const tdSx = {
  padding: '4px 0',
  fontSize: '1.4rem',
  color: abyssColors.primary[400],
  border: `1px solid ${abyssColors.primary[100]}`,
  verticalAlign: 'middle' as const,
  textAlign: 'center' as const,
};

export const BulkEditTable = ({
  pois,
  columns,
  values,
  errors,
  setFieldValue,
  setValues,
  visibleColumnKeys,
  onVisibleColumnsChange,
  initialValues,
}: Props) => {
  const [activeColumn, setActiveColumn] = useState<BulkEditColumnDef | null>(null);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [columnLastValues, setColumnLastValues] = useState<Record<string, string>>({});

  const visibleColumns = useMemo(
    () => columns.filter((col) => visibleColumnKeys.includes(col.key)),
    [columns, visibleColumnKeys]
  );

  const stickyMeta = useMemo(() => {
    const visibleMandatory = visibleColumns.filter((col) => col.isMandatory);
    const lastMandatoryKey =
      visibleMandatory.length > 0 ? visibleMandatory[visibleMandatory.length - 1].key : null;

    const leftOffsets: Record<string, number> = {};
    let cumulative = 0;
    for (const col of visibleColumns) {
      if (!col.isMandatory) break;
      leftOffsets[col.key] = cumulative;
      cumulative += MANDATORY_COL_WIDTHS[col.key] ?? 120;
    }

    return { leftOffsets, lastMandatoryKey };
  }, [visibleColumns]);

  const handleBulkColumnEdit = useCallback((column: BulkEditColumnDef) => {
    setActiveColumn(column);
  }, []);

  const handleBulkApply = useCallback(
    (value: string) => {
      if (!activeColumn) return;
      pois.forEach((poi) => {
        setFieldValue(`${poi.id}.${activeColumn.key}`, value);
      });
    },
    [activeColumn, setFieldValue, pois],
  );

  const handleResetRow = useCallback(
    (poiId: string) => {
      setValues({ ...values, [poiId]: { ...(initialValues[poiId] ?? {}) } });
    },
    [setValues, values, initialValues],
  );

  const dialogColumns = useMemo(
    () =>
      columns.map((col) => ({
        key: col.key,
        name: col.label,
        isSelected: visibleColumnKeys.includes(col.key),
        templateFieldKey: col.key,
      })),
    [columns, visibleColumnKeys]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'auto',
            border: `1px solid ${abyssColors.primary[100]}`,
          }}
        >
          <thead>
            <tr>
              {visibleColumns.map((col) => {
                const isSticky = col.isMandatory === true;
                const isLastMandatory = col.key === stickyMeta.lastMandatoryKey;
                const stickyStyle = isSticky
                  ? {
                      position: 'sticky' as const,
                      left: stickyMeta.leftOffsets[col.key] ?? 0,
                      zIndex: 2,
                      backgroundColor: '#fff',
                      minWidth: MANDATORY_COL_WIDTHS[col.key] ?? 120,
                      ...(isLastMandatory ? { borderRight: '2px solid rgba(0,0,0,0.08)' } : {}),
                    }
                  : {};
                return (
                  <th key={col.key} style={{ ...thSx, ...stickyStyle }}>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      {col.label}
                      {col.isBulkEditable && (
                        <Tooltip title="Apply to all rows">
                          <IconButton
                            size="small"
                            onClick={() => handleBulkColumnEdit(col)}
                            sx={{ padding: '2px' }}
                          >
                            <EditIcon sx={{ fontSize: 14, color: abyssColors.primary[300] }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </th>
                );
              })}
              <th style={{ ...thSx, width: 40, textAlign: 'right' as const }}>
                <Tooltip title="Column settings">
                  <IconButton size="small" onClick={() => setColumnSettingsOpen(true)}>
                    <SettingsIcon sx={{ fontSize: 18, color: abyssColors.primary[300] }} />
                  </IconButton>
                </Tooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {pois.map((poi) => {
              const currentPoiValues = values[poi.id] ?? {};
              const initialPoiValues = initialValues[poi.id] ?? {};
              const hasRowChanges = Object.keys(currentPoiValues).some(
                (key) => currentPoiValues[key] !== initialPoiValues[key],
              );
              const rowBg = hasRowChanges ? '#F5F8FF' : '#fff';

              return (
                <tr key={poi.id}>
                  {visibleColumns.map((col) => {
                    const rawValue = (values[poi.id]?.[col.key] as string) ?? '';
                    const fieldError = (errors as Record<string, Record<string, string>>)[poi.id]?.[
                      col.key
                    ];

                    const isSticky = col.isMandatory === true;
                    const isLastMandatory = col.key === stickyMeta.lastMandatoryKey;
                    const isCellChanged = currentPoiValues[col.key] !== initialPoiValues[col.key];
                    const cellBg = isCellChanged ? '#E8F0FE' : rowBg;

                    const stickyStyle = isSticky
                      ? {
                          position: 'sticky' as const,
                          left: stickyMeta.leftOffsets[col.key] ?? 0,
                          zIndex: 1,
                          backgroundColor: cellBg,
                          minWidth: MANDATORY_COL_WIDTHS[col.key] ?? 120,
                          ...(isLastMandatory ? { borderRight: '2px solid rgba(0,0,0,0.08)' } : {}),
                        }
                      : { backgroundColor: cellBg };

                    return (
                      <td key={col.key} style={{ ...tdSx, ...stickyStyle }}>
                        <BulkEditCell
                          column={col}
                          value={rawValue}
                          onChange={(val) => setFieldValue(`${poi.id}.${col.key}`, val)}
                          error={fieldError}
                        />
                      </td>
                    );
                  })}
                  <td style={{ ...tdSx, width: 40, backgroundColor: rowBg }}>
                    {hasRowChanges && (
                      <Tooltip title="Reset row">
                        <IconButton size="small" onClick={() => handleResetRow(poi.id)}>
                          <RestartAltIcon sx={{ fontSize: 18, color: abyssColors.primary[300] }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {activeColumn && (
        <BulkColumnEditPopover
          open={Boolean(activeColumn)}
          column={activeColumn}
          lastValue={columnLastValues[activeColumn.key] ?? ''}
          onApply={(value) => {
            handleBulkApply(value);
            setColumnLastValues((prev) => ({ ...prev, [activeColumn.key]: value }));
          }}
          onClose={() => setActiveColumn(null)}
        />
      )}

      <ColumnsSelectionDialog
        open={columnSettingsOpen}
        columns={dialogColumns}
        heading="Configure columns"
        subHeading="Select columns to display in the bulk edit table:"
        applyButtonText="Apply"
        onApply={onVisibleColumnsChange}
        onClose={() => setColumnSettingsOpen(false)}
      />
    </Box>
  );
};
